import { Theme, css } from '@emotion/react';

import { useCallback, useEffect, useRef, useState } from 'react';

const adfitCSS = (theme: Theme) => css`
  font-size: 0;

  .kakao_ad_loaded {
    box-sizing: content-box;
    border-top: 8px solid ${theme.border.space};
    border-bottom: 8px solid ${theme.border.space};
  }
`;

const Adfit = ({
  type = 'default',
}: {
  type?: 'story' | 'library' | 'default' | 'episodeEnding';
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const insRef = useRef<HTMLModElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  const adIds = {
    default: process.env.NEXT_PUBLIC_ADFIT_AD_UNIT_ID,
    story: process.env.NEXT_PUBLIC_ADFIT_STORY_DETAIL_UNIT_ID,
    library: process.env.NEXT_PUBLIC_ADFIT_LIBRARY_UNIT_ID,
    episodeEnding: process.env.NEXT_PUBLIC_ADFIT_EPISODE_ENDING_UNIT_ID,
  };

  // Intersection Observer: 뷰포트 진입 시에만 광고 로드
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  // 뷰포트 진입 후 AdFit 스크립트 로드
  const loadAdfit = useCallback(() => {
    if (!isVisible) return;

    const existing = document.querySelector(
      'script[src*="t1.daumcdn.net/kas/static/ba.min.js"]'
    );

    if (!existing) {
      const script = document.createElement('script');
      script.src = '//t1.daumcdn.net/kas/static/ba.min.js';
      script.async = true;
      script.type = 'text/javascript';
      document.body.appendChild(script);
    } else {
      // 스크립트가 이미 있으면 AdFit 재호출
      if (window.adfit && typeof window.adfit === 'function') {
        window.adfit('create', {
          adUnitId: adIds[type],
          containerElement: insRef.current,
        });
      }
    }
  }, [isVisible, type]);

  useEffect(() => {
    loadAdfit();
  }, [loadAdfit]);

  // MutationObserver: 광고 로드 완료 감지
  useEffect(() => {
    if (!insRef.current || !isVisible) return;

    const observer = new MutationObserver(() => {
      if (insRef.current) {
        insRef.current.className = 'kakao_ad_loaded';
        observer.disconnect();
      }
    });
    observer.observe(insRef.current, {
      attributes: true,
      attributeFilter: ['style'],
    });

    return () => {
      observer.disconnect();
    };
  }, [isVisible]);

  return (
    <div ref={containerRef} css={adfitCSS}>
      {isVisible && (
        <ins
          ref={insRef}
          className="kakao_ad_area"
          style={{ display: 'none' }}
          data-ad-unit={adIds[type]}
          data-ad-width="320"
          data-ad-height="50"
        />
      )}
    </div>
  );
};

export default Adfit;
